import { lt } from './types';
import type { TourStep } from './types';

/**
 * First-run guided tour — one stop to point at a nav button ("how you get
 * there"), immediately followed by a stop that actually navigates there and
 * spotlights the page itself ("what's on it"). Depth still lives in the Help
 * Center chapters; this walks the real UI instead of just describing it.
 *
 * Nav-item targets use `data-tour-nav`, which exists twice in the DOM (rail
 * + dock) — on a phone the dock folds everything past the first four items
 * behind "Mehr" (`packages/ui` `NavMenu`), so `Coachmark`'s `revealSelector`
 * (wired in `GuidedTour.tsx`) expands that overflow automatically the moment
 * a step's target isn't rendered yet.
 */
export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: lt('Willkommen, Kommandant', 'Welcome, commander'),
    body: lt(
      'Ein Rundgang durch jeden Bereich des Cockpits — jederzeit überspringbar. Alles kommt danach noch einmal zum Nachschlagen ins Hilfe-Center.',
      'A walk through every section of the cockpit — skippable at any time. Everything comes back afterward for reference in the Help Center.',
    ),
  },

  // Overview ----------------------------------------------------------------
  {
    id: 'overview-nav',
    targetSelector: '[data-tour-nav="overview"]',
    navTarget: 'overview',
    title: lt('Übersicht', 'Overview'),
    body: lt(
      'Dieser Knopf bringt dich jederzeit zurück zum Kommandozentrum — deiner Startseite.',
      'This button always brings you back to Command Center — your home screen.',
    ),
  },
  {
    id: 'resources',
    targetSelector: '[data-tour="resource-bar"]',
    navTarget: 'overview',
    title: lt('Deine Ressourcen', 'Your resources'),
    body: lt(
      'Titan, Silikat, Isotop, Energie und Lagerkapazität laufen hier live mit. Ein Klick auf einen Wert öffnet die Statistik dazu.',
      'Titan, silicate, isotope, power and storage capacity tick along here live. Click a value to open its stats.',
    ),
  },

  // Buildings -----------------------------------------------------------------
  {
    id: 'buildings-nav',
    targetSelector: '[data-tour-nav="buildings"]',
    title: lt('Gebäude — so kommst du hin', 'Buildings — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du jederzeit die Gebäude-Ansicht deines Heimatplaneten.',
      'This button always takes you to your home planet\'s buildings view.',
    ),
  },
  {
    id: 'buildings-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'buildings',
    title: lt('Gebäude', 'Buildings'),
    body: lt(
      'Hier baust du Titan-Schmelze, Kraftwerk, Lager und alles Weitere aus — eine Warteschlange pro Planet, ein Auftrag nach dem anderen. Jede Karte zeigt Kosten, Bauzeit und den nächsten Effekt.',
      'This is where you upgrade the titan smelter, power plant, storage and everything else — one queue per planet, one job after another. Every card shows cost, build time and the next effect.',
    ),
  },

  // Research ------------------------------------------------------------------
  {
    id: 'research-nav',
    targetSelector: '[data-tour-nav="research"]',
    title: lt('Forschung — so kommst du hin', 'Research — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du die Forschungs- und Talentansicht.',
      'This button takes you to the research and talent view.',
    ),
  },
  {
    id: 'research-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'research',
    title: lt('Forschung & Talente', 'Research & talents'),
    body: lt(
      'Forschung läuft global für dein ganzes Reich — nur eine Technologie gleichzeitig. Hier findest du auch den Talentbaum: jeder Levelaufstieg gibt dir einen Punkt.',
      'Research runs globally for your whole empire — only one technology at a time. This is also where the talent tree lives: every level-up grants you a point.',
    ),
  },

  // Shipyard --------------------------------------------------------------------
  {
    id: 'shipyard-nav',
    targetSelector: '[data-tour-nav="shipyard"]',
    title: lt('Werft — so kommst du hin', 'Shipyard — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du die Werft, in der du deine Flotte baust.',
      'This button takes you to the shipyard, where you build your fleet.',
    ),
  },
  {
    id: 'shipyard-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'shipyard',
    title: lt('Werft', 'Shipyard'),
    body: lt(
      'Sonden, Sammelschiffe, Kampfschiffe — hier baust du deine Flotte, auch im Stapel (×1/×5/×25). Werft und Schiffshangar bestimmen zusammen, wie viele Flotten gleichzeitig unterwegs sein können.',
      'Probes, collectors, combat ships — this is where you build your fleet, in batches too (×1/×5/×25). The shipyard and hangar together set how many fleets can be away at once.',
    ),
  },

  // Defense -------------------------------------------------------------------
  {
    id: 'defense-nav',
    targetSelector: '[data-tour-nav="defense"]',
    title: lt('Abwehr — so kommst du hin', 'Defense — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du deine planetare Abwehr.',
      'This button takes you to your planetary defenses.',
    ),
  },
  {
    id: 'defense-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'defense',
    title: lt('Abwehr', 'Defense'),
    body: lt(
      'Geschütze kämpfen automatisch mit, sobald dich jemand angreift — auch offline. Abfangraketen und Schildkuppeln wehren Kometen automatisch ab, sofern vorhanden.',
      'Cannons automatically join the fight the moment someone attacks you — even offline. Interceptor missiles and shield domes automatically defend against comets, if you have any.',
    ),
  },

  // Sector map ------------------------------------------------------------------
  {
    id: 'sector-nav',
    targetSelector: '[data-tour-nav="sector"]',
    title: lt('Sektor-Karte — so kommst du hin', 'Sector map — how to get there'),
    body: lt(
      'Über diesen Knopf öffnest du die Galaxiekarte.',
      'This button opens the galaxy map.',
    ),
  },
  {
    id: 'sector-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'sector',
    title: lt('Sektor-Karte', 'Sector map'),
    body: lt(
      'Ringe, Systeme, Koordinaten — hier siehst du andere Spieler, NPC-Kolonien und freie Plätze zum Kolonisieren. Ab Level 36 (Ring 2) kannst du hier eigene Kolonien gründen.',
      'Rings, systems, coordinates — this is where you see other players, NPC colonies and empty slots to colonize. From level 36 (ring 2) you can found colonies here yourself.',
    ),
  },

  // Pirate camps ----------------------------------------------------------------
  {
    id: 'camps-nav',
    targetSelector: '[data-tour-nav="camps"]',
    title: lt('Piratenlager — so kommst du hin', 'Pirate camps — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du die Piratenlager in deinem Heimatsystem.',
      'This button takes you to the pirate camps in your home system.',
    ),
  },
  {
    id: 'camps-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'camps',
    title: lt('Piratenlager', 'Pirate camps'),
    body: lt(
      'Bis zu drei Lager im Heimatsystem — spähen, angreifen, Beute machen. Ein guter Ort, um deine erste Flotte auszuprobieren, ohne gleich gegen echte Spieler anzutreten.',
      'Up to three camps in your home system — scout, attack, loot. A good place to try out your first fleet without going up against real players right away.',
    ),
  },

  // Trade -------------------------------------------------------------------------
  {
    id: 'trade-nav',
    targetSelector: '[data-tour-nav="trade"]',
    title: lt('Händler — so kommst du hin', 'Trade — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du den Händler, sobald du ihn freigeschaltet hast.',
      'This button takes you to the trader, once you\'ve unlocked it.',
    ),
  },
  {
    id: 'trade-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'trade',
    title: lt('Händler', 'Trade'),
    body: lt(
      'Sobald die Händler-Relaisstation (Stufe 1) auf deinem Heimatplaneten steht, kannst du hier Rohstoffe untereinander umtauschen. Sie schaltet außerdem das Casino frei.',
      'Once the trade relay (level 1) is built on your home planet, you can exchange resources here. It also unlocks the casino.',
    ),
  },

  // Casino ---------------------------------------------------------------------------
  {
    id: 'casino-nav',
    targetSelector: '[data-tour-nav="casino"]',
    title: lt('Casino — so kommst du hin', 'Casino — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du das Casino, sobald es freigeschaltet ist.',
      'This button takes you to the casino, once it\'s unlocked.',
    ),
  },
  {
    id: 'casino-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'casino',
    title: lt('Casino', 'Casino'),
    body: lt(
      'Spielautomaten, Roulette und High-Low mit Credit-Einsatz — braucht Händler-Relaisstation und das Casino-Gebäude (beide Stufe 1). Alle Spiele sind auf rund 92 % Auszahlungsquote kalibriert.',
      'Slots, roulette and high-low with credit stakes — needs the trade relay and the casino building (both level 1). Every game is calibrated to roughly a 92% payout rate.',
    ),
  },

  // Shop ------------------------------------------------------------------------------
  {
    id: 'shop-nav',
    targetSelector: '[data-tour-nav="shop"]',
    title: lt('Shop — so kommst du hin', 'Shop — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du den Chronos-Shop.',
      'This button takes you to the Chronos shop.',
    ),
  },
  {
    id: 'shop-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'shop',
    title: lt('Chronos-Shop', 'Chronos shop'),
    body: lt(
      'Chronos-Kristalle kaufen laufende Bau-, Forschungs- und Werftaufträge frei — Zeit gegen die Warteschlange. Chronos lässt sich auch im Spiel selbst verdienen, nicht nur kaufen.',
      'Chronos crystals instantly finish a running build, research or shipyard order — time against the queue. Chronos can also be earned in-game, not only bought.',
    ),
  },

  // Messages ----------------------------------------------------------------------------
  {
    id: 'messages-nav',
    targetSelector: '[data-tour-nav="messages"]',
    title: lt('Nachrichten — so kommst du hin', 'Messages — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du dein Postfach. Eine Zahl zeigt ungelesene Nachrichten.',
      'This button takes you to your inbox. A number marks unread messages.',
    ),
  },
  {
    id: 'messages-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'messages',
    title: lt('Nachrichten', 'Messages'),
    body: lt(
      'Kampf-, Spionage- und Systemnachrichten landen hier — jeder Bericht ausformuliert, keine rohen Zahlenlisten. Ein Spionagebericht bietet sogar einen Direktangriff-Einstieg an.',
      'Combat, espionage and system messages land here — every report written out, no raw number dumps. An espionage report even offers a direct-attack shortcut.',
    ),
  },

  // Settings ------------------------------------------------------------------------------
  {
    id: 'settings-nav',
    targetSelector: '[data-tour-nav="settings"]',
    title: lt('Einstellungen — so kommst du hin', 'Settings — how to get there'),
    body: lt(
      'Über diesen Knopf erreichst du Kommandantenname, Sprache, Darstellung und Konto.',
      'This button takes you to your commander name, language, appearance and account.',
    ),
  },
  {
    id: 'settings-content',
    targetSelector: '[data-tour="view-header"]',
    navTarget: 'settings',
    title: lt('Einstellungen', 'Settings'),
    body: lt(
      'Kommandantenname, Sprache des Hilfe-Centers, Darstellung, Barrierefreiheit und dein Konto (Google/E-Mail verknüpfen, Spielstand exportieren) — alles an einem Ort.',
      'Commander name, Help Center language, appearance, accessibility and your account (link Google/email, export your save) — all in one place.',
    ),
  },

  // Help ------------------------------------------------------------------------------------
  {
    id: 'help',
    targetSelector: '[data-tour-nav="help"]',
    title: lt('Hilfe jederzeit griffbereit', 'Help always within reach'),
    body: lt(
      'Dieser Knopf öffnet jederzeit das vollständige Hilfe-Center mit allen Kapiteln, einer Suche und den häufigsten Fragen — und startet diese Tour bei Bedarf erneut.',
      'This button always opens the full Help Center with every chapter, a search, and the most common questions — and can restart this tour whenever you like.',
    ),
  },
];
